/* eslint-disable */
import { useState, useCallback } from 'react';
import { CameraCapture } from './CameraCapture';

export function CardPhotoSearch({ onSearchResults, searchCards }) {
  const [showCamera, setShowCamera] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [processingError, setProcessingError] = useState('');

  const handleCapture = useCallback(
    async (imageBlob) => {
      setIsProcessing(true);
      setProcessingError('');
      setExtractedText('');

      try {
        // Dynamically import Tesseract to lazy-load it
        const Tesseract = await import('tesseract.js');
        const worker = Tesseract.createWorker();
        await worker.load();
        await worker.loadLanguage('eng');
        await worker.initialize('eng');

        const result = await worker.recognize(imageBlob);
        const fullText = result.data.text;

        await worker.terminate();

        setExtractedText(fullText);

        // Extract card name from OCR text
        // Card names typically appear near the top of the card
        const lines = fullText
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0 && line.length < 100);

        // Look for likely card names (first few lines usually contain it)
        let cardName = lines[0] || '';

        // Filter out common non-name text
        const filterWords = [
          'hp', 'stage', 'ability', 'pokémon', 'pokemon', 'attack', 'weakness',
          'resistance', 'retreat', 'cost', 'illustrator', 'set', 'edition',
        ];

        if (cardName && filterWords.some(w => cardName.toLowerCase().includes(w))) {
          cardName = lines[1] || cardName;
        }

        // Clean up the card name
        cardName = cardName
          .replace(/[^a-zA-Z0-9\s\-.]/g, '') // Remove special chars
          .replace(/\d+\s*hp$/i, '') // Remove HP
          .trim();

        if (!cardName || cardName.length < 2) {
          setProcessingError('Could not extract card name from image. Try a clearer photo or search manually.');
          setIsProcessing(false);
          return;
        }

        // Search for cards with extracted name
        const results = await searchCards(cardName);

        onSearchResults({
          cardName,
          fullText,
          results,
        });

        setShowCamera(false);
      } catch (err) {
        console.error('OCR error:', err);
        setProcessingError('Error processing image: ' + (err.message || 'Unknown error'));
      } finally {
        setIsProcessing(false);
      }
    },
    [searchCards, onSearchResults]
  );

  if (showCamera) {
    return <CameraCapture onCapture={handleCapture} onClose={() => setShowCamera(false)} />;
  }

  return (
    <div>
      <button
        onClick={() => setShowCamera(true)}
        disabled={isProcessing}
        style={{
          padding: '7px 14px',
          borderRadius: 'var(--r-md)',
          background: 'var(--c-electric)',
          color: '#fff',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--fs-13)',
          fontWeight: 700,
          border: 'none',
          cursor: isProcessing ? 'not-allowed' : 'pointer',
          opacity: isProcessing ? 0.6 : 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        {isProcessing ? '⟳ Processing...' : '📷 Camera'}
      </button>

      {processingError && (
        <div
          style={{
            marginTop: '10px',
            padding: '12px',
            background: 'var(--c-fire-bg)',
            color: 'var(--c-fire-ink)',
            borderRadius: 'var(--r-md)',
            fontSize: 'var(--fs-12)',
            border: '1px solid var(--c-fire)',
          }}
        >
          {processingError}
        </div>
      )}

      {extractedText && (
        <div
          style={{
            marginTop: '10px',
            padding: '12px',
            background: 'var(--c-mist)',
            borderRadius: 'var(--r-md)',
            fontSize: 'var(--fs-12)',
            color: 'var(--c-fg-2)',
            maxHeight: '100px',
            overflow: 'auto',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '6px' }}>Extracted text:</div>
          <code style={{ fontSize: 'var(--fs-11)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {extractedText.substring(0, 200)}
            {extractedText.length > 200 ? '...' : ''}
          </code>
        </div>
      )}
    </div>
  );
}
